<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useClientsList } from '@/api'
import { EditFieldModal } from '@/components/CreatorDetail'
import { formatRevenueTierLabel } from '@/lib/adapters/clients.adapter'
import { SelectComponent } from '@/ui-library'

const props = defineProps<{
  clientId: number
  revenueTierId: number
  isLoading: boolean
}>()

const emits = defineEmits<{
  save: [tierId: number]
}>()

const isOpen = defineModel<boolean>('modelValue', { default: false })

const { data: clients, isLoading: isClientsLoading } = useClientsList(() => ({
  query: props.clientId ? String(props.clientId) : '',
  size: 20,
}))

const selectedTierId = ref<number | undefined>(props.revenueTierId)

const availableTiers = computed(() => {
  const client = clients.value.find((c) => c.id === props.clientId)
  return client?.revenueTiers ?? []
})

const tierOptions = computed(() => availableTiers.value.map((t) => ({ label: formatRevenueTierLabel(t), value: t.id })))

const standardOption = [{ label: 'Standard - 100%', value: 'standard' }]

watch(
  [() => isOpen.value, availableTiers, () => props.revenueTierId],
  ([open, tiers, tierId]) => {
    if (open) {
      if (tiers.length > 0) {
        const isValid = tiers.some((t) => t.id === tierId)
        selectedTierId.value = isValid ? tierId : undefined
      } else {
        selectedTierId.value = tierId
      }
    }
  },
  { immediate: true },
)

const onSave = () => {
  if (selectedTierId.value !== undefined) {
    emits('save', selectedTierId.value)
  }
}
</script>
<template>
  <EditFieldModal v-model="isOpen" title="Edit revenue tier" :is-loading="isLoading" @save="onSave">
    <SelectComponent v-if="isClientsLoading" model-value="" :options="[]" placeholder="Loading..." disabled />
    <SelectComponent
      v-else-if="availableTiers.length > 0"
      v-model="selectedTierId"
      :options="tierOptions"
      placeholder="Select revenue tier"
      :teleported="false"
    />
    <SelectComponent v-else model-value="standard" :options="standardOption" disabled />
  </EditFieldModal>
</template>
